import {
    type RegisteredRouter,
    trimPathRight,
    useRouter,
    useRouterState,
    type ValidateLinkOptions,
} from '@tanstack/react-router';

import { movedPagesOf } from '../movedPages';
import type { AppLinkOptions } from './linkOptions';

type IsRouteActiveOptions = {
    /** Match only this exact route, rather than it and everything beneath it. */
    exact?: boolean;
};

/**
 * Whether the user is on the route or beneath it (or exactly on it); search is ignored and segments match whole. A link
 * to a page with a second route (`createAppRouter`'s `movedPages`) is active under both.
 */
export function useIsRouteActive<const T>(
    options: ValidateLinkOptions<RegisteredRouter, T> | AppLinkOptions,
    { exact = false }: IsRouteActiveOptions = {},
): boolean {
    const router = useRouter();
    const { to } = options as { to?: unknown };
    const moved = movedPagesOf(router).find(({ from }) => from === to);
    const movedOptions = moved && { ...options, to: moved.to };

    // Selects the answer from the whole state `matchRoute` reads, so callers re-render only when it flips.
    return useRouterState({
        select: () =>
            [options, movedOptions].some(
                (link) => link && Boolean(router.matchRoute(link as never, { fuzzy: !exact, includeSearch: false })),
            ),
    });
}

/**
 * Internal, for a link that marks itself with the router's `activeProps`: whether the URL is the link's page at its
 * second route (`createAppRouter`'s `movedPages`). The router compares the link's own URL, so there it marks nothing.
 * Read as the router's exact mark is: from the URL a navigation goes to, without the search or a trailing slash.
 */
export function useIsAtSecondRoute(link: AppLinkOptions): boolean {
    const router = useRouter();
    const moved = movedPagesOf(router).find(({ from }) => from === link.to);
    const pathname = moved && router.buildLocation({ ...link, to: moved.to } as never).pathname;
    return useRouterState({
        select: ({ location }) =>
            pathname !== undefined && trimPathRight(location.pathname) === trimPathRight(pathname),
    });
}
